import { notification } from "antd";
import { AUTH_MODE, LOGIN_PATH, LOGIN_URL, REACT_APP_API_URL } from "../config";

export interface AuthStrategy {
  baseUrl(): string;
  apiUrl(path?: string): string;
  assetUrl(path?: string): string;
  loginUrl(returnUrl?: string): string;
  headers(): Record<string, string>;
  onUnauthorized(): void;
  onSessionOk(): void;
  isRedirecting(): boolean;
  trackRequest(userSignal?: AbortSignal | null): {
    signal: AbortSignal;
    done: () => void;
  };
}

// env value, then stored reqUrl, then page origin
const baseUrl = () =>
  REACT_APP_API_URL ||
  localStorage.getItem("reqUrl") ||
  window.location.origin;

const serverRoot = () => baseUrl().replace(/\/+$/, "");

const trimSlashes = (path: string) => path.replace(/^\/+/, "");

const apiUrl = (path = "") =>
  path ? `${serverRoot()}/api/${trimSlashes(path)}` : `${serverRoot()}/api`;

const assetUrl = (path = "") => `${serverRoot()}/${trimSlashes(path)}`;

const loginUrl = (returnUrl: string = window.location.href) => {
  const base = LOGIN_URL || `${serverRoot()}/${trimSlashes(LOGIN_PATH)}`;
  const separator = base.includes("?") ? "&" : "?";
  return `${base}${separator}return_url=${encodeURIComponent(returnUrl)}`;
};

const LOGIN_ATTEMPT_KEY = "auth:loginAttempt";
// a 401 this soon after a login redirect means the login did not stick
const LOOP_WINDOW_MS = 30 * 1000;

let redirecting = false;
const inflight = new Set<AbortController>();

const readLoginAttempt = (): number => {
  try {
    return Number(sessionStorage.getItem(LOGIN_ATTEMPT_KEY)) || 0;
  } catch (e) {
    return 0;
  }
};

const writeLoginAttempt = (value: number | null) => {
  try {
    if (value === null) sessionStorage.removeItem(LOGIN_ATTEMPT_KEY);
    else sessionStorage.setItem(LOGIN_ATTEMPT_KEY, String(value));
  } catch (e) {
    // storage unavailable
  }
};

const trackRequest = (userSignal?: AbortSignal | null) => {
  const controller = new AbortController();
  inflight.add(controller);

  if (userSignal) {
    if (userSignal.aborted) controller.abort();
    else
      userSignal.addEventListener("abort", () => controller.abort(), {
        once: true,
      });
  }

  return { signal: controller.signal, done: () => inflight.delete(controller) };
};

const onUnauthorized = () => {
  // only the first 401 redirects
  if (redirecting) return;
  redirecting = true;

  inflight.forEach((controller) => controller.abort());
  inflight.clear();

  const lastAttempt = readLoginAttempt();
  if (lastAttempt && Date.now() - lastAttempt < LOOP_WINDOW_MS) {
    // back from login and still 401, stop instead of looping
    writeLoginAttempt(null);
    notification.error({
      key: "auth-loop",
      message: "Sign-in problem",
      description:
        "You signed in, but the server did not accept the session. Please reload the page to try again.",
      duration: 0,
    });
    return;
  }

  writeLoginAttempt(Date.now());
  window.location.assign(loginUrl(window.location.href));
};

const onSessionOk = () => {
  if (readLoginAttempt()) writeLoginAttempt(null);
};

const isRedirecting = () => redirecting;

const shared = {
  baseUrl,
  apiUrl,
  assetUrl,
  loginUrl,
  onUnauthorized,
  onSessionOk,
  isRedirecting,
  trackRequest,
};

// the browser sends the session cookie
const cookieStrategy: AuthStrategy = {
  ...shared,
  headers: () => ({}),
};

// the cookie is not readable on localhost, so send the stored jwt
const headerStrategy: AuthStrategy = {
  ...shared,
  headers: (): Record<string, string> => {
    const token = localStorage.getItem("Authorization");
    return token ? { Authorization: `Bearer ${token}` } : {};
  },
};

const auth: AuthStrategy =
  AUTH_MODE === "header" ? headerStrategy : cookieStrategy;

export default auth;
